/* eslint-disable @typescript-eslint/no-explicit-any */
import styled from 'styled-components';
import useHorizontalBarData from 'hooks/useHorizontalBarData';
import useHorizontalBarLabel from 'hooks/useHorizontalBarLabel';
import { chartColor, color } from 'asserts/styles/colors';
import {
  ageLabels,
  careerLabels,
  genderLabels,
  elementLabels,
  middleLabels
} from 'consts/constants';
import { commonTooltipOptions } from 'consts/chartOptions';
import { useAtomValue } from 'jotai';
import productAtom from 'atom/productAtom';

import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ChartDetailProps {
  selectedLabel: string | null;
  detailData: any;
  selectedData: string | null;
  selectedChartType: string | null;
}

const PersonnelModal = ({
  selectedLabel,
  detailData,
  selectedData,
  selectedChartType
}: ChartDetailProps) => {
  let chartData = null;
  let datasets: any[] = [];
  for (const key in detailData) {
    if (key === selectedData) {
      chartData = detailData[key];
      break;
    }
  }

  const productNumber = useAtomValue(productAtom);

  const { graphOrange, graphGreen } = chartColor;
  if (selectedChartType === 'career') {
    datasets = [
      {
        title: '연령대',
        data: [
          chartData.age.teenager,
          chartData.age.twenty,
          chartData.age.thirty,
          chartData.age.fifty,
          chartData.age.forty,
          chartData.age.sixty
        ],
        colors: graphGreen,
        labels: ageLabels
      },
      {
        title: '성비',
        data: [chartData.gender.male, chartData.gender.female],
        colors: graphOrange,
        labels: genderLabels
      }
    ];
  } else if (selectedChartType === 'age') {
    if (productNumber === 4 || productNumber === 5) {
      datasets = [
        {
          title: '근속연수',
          data: [
            chartData.career.five,
            chartData.career.ten,
            chartData.career.fifteen,
            chartData.career.twenty,
            chartData.career.gttwenty
          ],
          colors: graphGreen,
          labels: careerLabels
        },
        {
          title: '성비',
          data: [chartData.gender.male, chartData.gender.female],
          colors: graphOrange,
          labels: genderLabels
        }
      ];
    } else {
      datasets = [
        {
          title: '성비',
          data: [chartData.gender.male, chartData.gender.female],
          colors: graphOrange,
          labels: genderLabels
        }
      ];
    }
  } else if (selectedChartType === 'gender') {
    if (productNumber === 4 || productNumber === 5) {
      datasets = [
        {
          title: '근속연수',
          data: [
            chartData.career.five,
            chartData.career.ten,
            chartData.career.fifteen,
            chartData.career.twenty,
            chartData.career.gttwenty
          ],
          colors: graphOrange,
          labels: careerLabels
        },
        {
          title: '연령대',
          data: [
            chartData.age.teenager,
            chartData.age.twenty,
            chartData.age.thirty,
            chartData.age.fifty,
            chartData.age.forty,
            chartData.age.sixty
          ],
          colors: graphGreen,
          labels: ageLabels
        }
      ];
    } else if (productNumber === 8) {
      datasets = [
        {
          title: '학년별',
          data: [
            chartData.grade.grade1,
            chartData.grade.grade2,
            chartData.grade.grade3,
            chartData.grade.grade4,
            chartData.grade.grade5,
            chartData.grade.grade6
          ],
          colors: graphGreen,
          labels: elementLabels
        }
      ];
    } else if (productNumber === 9) {
      datasets = [
        {
          title: '학년별',
          data: [
            chartData.grade.grade1,
            chartData.grade.grade2,
            chartData.grade.grade3
          ],
          colors: graphGreen,
          labels: middleLabels
        }
      ];
    } else {
      datasets = [
        {
          title: '연령대',
          data: [
            chartData.age.teenager,
            chartData.age.twenty,
            chartData.age.thirty,
            chartData.age.fifty,
            chartData.age.forty,
            chartData.age.sixty
          ],
          colors: graphGreen,
          labels: ageLabels
        }
      ];
    }
  } else if (selectedChartType === 'grade') {
    datasets = [
      {
        title: '성비',
        data: [chartData.gender.male, chartData.gender.female],
        colors: graphOrange,
        labels: genderLabels
      }
    ];
  }

  const options = {
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: {
        display: false
      },
      tooltip: commonTooltipOptions
    },
    scales: {
      x: {
        display: false,
        reverse: true,
        ticks: {
          stepSize: 500
        },
        max: chartData.total,
        afterDataLimits: (scale: any) => {
          scale.max = scale.max * 1.4;
        }
      },
      y: {
        display: false,
        grid: {
          display: false
        },
        ticks: {
          dislay: false,
          font: {
            size: 13,
            weight: 'bold',
            family: 'Noto Sans KR'
          }
        }
      }
    }
  };

  return (
    <>
      <ModalTitle>{`${selectedLabel} 상세 정보`}</ModalTitle>
      {datasets?.map((data, index) => (
        <PersonnelModalChart
          header={careerLabels[0]}
          data={data.data}
          key={index}
          title={data.title}
          colors={data.colors}
          labels={data.labels}
          options={options}
          selectedChartType={selectedChartType}
          productNumber={productNumber}
        />
      ))}
    </>
  );
};

const PersonnelModalChart = ({
  title,
  colors,
  options,
  labels,
  data: chartData,
  selectedChartType,
  productNumber
}: any) => {
  const data = {
    labels,
    datasets: [
      {
        label: labels,
        data: chartData,
        backgroundColor: colors,
        borderColor: colors,
        barThickness: 6,
        borderRadius: 3
      }
    ]
  };

  const addDataPlugins = {
    id: 'addDataPlugins',
    afterDatasetsDraw: useHorizontalBarData()
  };

  const addLabelsData = {
    id: 'addLabelsData',
    beforeDatasetsDraw: useHorizontalBarLabel()
  };

  return (
    <>
      <ChartTitle>{title}</ChartTitle>
      <Wrapper
        $selectedChartType={selectedChartType}
        $productNumber={productNumber}
      >
        <Bar
          data={data}
          options={options}
          plugins={[addDataPlugins, addLabelsData]}
        />
      </Wrapper>
    </>
  );
};

export default PersonnelModal;

const Wrapper = styled.div<{
  $selectedChartType: 'age' | 'gender' | 'career';
  $productNumber: number;
}>`
  position: relative;
  margin: 4px auto 16px;
  background: ${color.white005};
  border-radius: 16px;
  height: ${(props) =>
    props.$selectedChartType === 'age'
      ? '117px'
      : props.$selectedChartType === 'gender'
        ? '118px'
        : '137px'};

  &:last-of-type {
    height: ${({ $selectedChartType, $productNumber }) =>
      $selectedChartType === 'gender' && $productNumber === 9
        ? '81px'
        : $selectedChartType === 'gender' && $productNumber === 8
          ? '139px'
          : $selectedChartType === 'gender'
            ? '138px'
            : '49px'};
  }
`;

const ModalTitle = styled.div`
  padding-bottom: 16px;
  text-align: center;
  font-weight: 700;
`;

const ChartTitle = styled.div`
  padding-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  border-bottom: 1px solid ${color.lightopacity30};
`;
